import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../LoopinoDesktopGuidelines.css';
import './DesktopStrategyPage.css';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import useCampaign from '../../../hooks/useCampaign';
import useMobileStrategy from '../../../hooks/useMobileStrategy';
import useApi from '../../../hooks/useApi';
import useLanguage from '../../../hooks/useLanguage';
import { useCapabilities } from '../../../capabilities/CapabilityContext';
import API_CONFIG from '../../../config/api';
import { Skeleton } from '../../effects';
import { getFirstMediaUrl, isVideoFile } from '../../../utils/mediaHelpers';
import DesktopCampaignSelector from '../DesktopCampaignSelector/DesktopCampaignSelector';
import DesktopHorizontalScroll from '../common/scroll/DesktopHorizontalScroll';
import useCompany from '../../../hooks/useCompany';
import { useDesktopAlert } from '../common/alerts/DesktopAlertContext';
import { DesktopAlertProvider } from '../common/alerts/DesktopAlertProvider';
import { hasCompanyDna } from '../../../utils/companyDna';

const STATUS_FALLBACKS = ['draft', 'active', 'paused', 'completed'];

const PlatformIcon = ({ platform, size = 14 }) => {
  const normalized = (platform || 'instagram').toString().toLowerCase();
  const key = normalized === 'twitter' ? 'x' : normalized;
  const icons = {
    instagram: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="2" y="2" width="20" height="20" rx="5" ry="5" stroke="currentColor" strokeWidth="2" />
        <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z" stroke="currentColor" strokeWidth="2" />
        <line x1="17.5" y1="6.5" x2="17.51" y2="6.5" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    facebook: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 2H15C13.6739 2 12.4021 2.52678 11.4645 3.46447C10.5268 4.40215 10 5.67392 10 7V10H7V14H10V22H14V14H17L18 10H14V7C14 6.73478 14.1054 6.48043 14.2929 6.29289C14.4804 6.10536 14.7348 6 15 6H18V2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
    linkedin: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M16 8C18.5259 8 20.5259 10 20.5259 12.5V19H16.5259V12.5C16.5259 11.5 15.5259 11 14.5259 11C13.5259 11 12.5259 11.5 12.5259 12.5V19H8.52588V8H12.5259V9.5C13.5259 8.5 14.5259 8 16 8Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <rect x="2" y="9" width="4" height="12" stroke="currentColor" strokeWidth="2" />
        <circle cx="4" cy="4" r="2" stroke="currentColor" strokeWidth="2" />
      </svg>
    ),
    x: (
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4 4L20 20M20 4L4 20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </svg>
    )
  };

  return icons[key] || icons.instagram;
};

const getPostProfileName = (post) => {
  if (!post) return '';

  const candidates = [
    post.profileName,
    post.profile_name,
    post.accountName,
    post.account_name,
    post.pageName,
    post.page_name,
    post.socialProfileName,
    post.social_profile_name,
    post.profile?.name,
    post.social_profile?.name,
    post.account?.name,
    post.page?.name,
    post.user?.name,
    post.username,
    post.owner?.name
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
    if (candidate && typeof candidate === 'object') {
      const value = candidate.displayName || candidate.username || candidate.name;
      if (value && typeof value === 'string' && value.trim()) {
        return value.trim();
      }
    }
  }

  return '';
};

const getPostScheduledDate = (post) => {
  if (!post) return null;
  const rawValue =
    post.scheduledDate ||
    post.scheduled_time ||
    post.scheduledTime ||
    post.scheduled_at ||
    post.scheduledAt;

  if (!rawValue) return null;

  const normalized = typeof rawValue === 'object' && rawValue.$date
    ? rawValue.$date
    : rawValue;

  const date = new Date(normalized);
  return Number.isNaN(date.getTime()) ? null : date;
};

const getWeekPostStatusInfo = (post) => {
  const rawStatus = post?.status;
  let normalizedStatus = '';

  if (typeof rawStatus === 'string') {
    normalizedStatus = rawStatus.toLowerCase();
  } else if (rawStatus && typeof rawStatus === 'object') {
    const candidate = rawStatus.value || rawStatus.current || rawStatus.status || rawStatus.state || rawStatus.name;
    normalizedStatus = candidate ? candidate.toString().toLowerCase() : '';
  } else if (rawStatus !== undefined && rawStatus !== null) {
    normalizedStatus = String(rawStatus).toLowerCase();
  }

  const isExplicitlyPosted = post?.posted === true;

  if (isExplicitlyPosted || normalizedStatus === 'published' || normalizedStatus === 'posted') {
    return {
      badgeVariant: 'posted',
      cardClass: 'posted'
    };
  }

  if (normalizedStatus === 'failed') {
    return {
      badgeVariant: 'failed',
      cardClass: 'failed'
    };
  }

  if (normalizedStatus === 'cancelled' || normalizedStatus === 'canceled') {
    return {
      badgeVariant: 'cancelled',
      cardClass: 'cancelled'
    };
  }

  if (normalizedStatus === 'publishing' || normalizedStatus === 'processing') {
    return {
      badgeVariant: 'publishing',
      cardClass: 'publishing'
    };
  }

  if (normalizedStatus === 'queued' || normalizedStatus === 'scheduled') {
    return {
      badgeVariant: 'scheduled',
      cardClass: 'scheduled'
    };
  }

  return {
    badgeVariant: 'pending',
    cardClass: 'draft'
  };
};

const normalizeIdValue = (value) => {
  if (value === undefined || value === null) return null;
  if (typeof value === 'string' || typeof value === 'number') return String(value);
  if (typeof value === 'object') {
    if (value.$oid) return String(value.$oid);
    if (value.id || value._id) return normalizeIdValue(value.id ?? value._id);
    if (typeof value.toString === 'function') {
      const stringified = value.toString();
      if (stringified && stringified !== '[object Object]') {
        return String(stringified);
      }
    }
  }
  return null;
};


const DesktopStrategyPageContent = ({ targetCampaignId = null, queryString = '', onNavigate }) => {
  const campaignHook = useCampaign();
  const strategyHook = useMobileStrategy();
  const { get: apiGet } = useApi();
  const { t, language } = useLanguage();
  const { getNavigationStateForHref, isAccessible } = useCapabilities();
  const { currentCompany, loading: companyLoading } = useCompany();
  const { notify } = useDesktopAlert();
  const dnaGateRef = useRef(false);

  const campaigns = campaignHook?.campaigns || [];
  const currentCampaign = campaignHook?.currentCampaign || null;
  const loadCampaigns = campaignHook?.loadCampaigns || (() => Promise.resolve());
  const switchCampaign = campaignHook?.switchCampaign || (() => {});
  const updateCampaign = campaignHook?.updateCampaign || null;
  const campaignLoading = campaignHook?.loading || false;
  const campaignError = campaignHook?.error || null;

  const {
    strategyData,
    isGeneratingStrategy,
    loading: strategyLoading,
    error: strategyError,
    mediaFiles = { pdf: [], image: [] },
    strategyImages = [],
    loadStrategyFromCampaign,
    generateStrategy
  } = strategyHook;

  const [selectedWeek, setSelectedWeek] = useState(0);
  const [imagePreview, setImagePreview] = useState(null);
  const [imageGallery, setImageGallery] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [campaignPosts, setCampaignPosts] = useState([]);
  const [postsLoading, setPostsLoading] = useState(false);
  const [postsError, setPostsError] = useState(null);
  const [postsRefreshKey, setPostsRefreshKey] = useState(0);
  const createPostCapability = getNavigationStateForHref('/desktop/create-post');
  const canStartPostCreation = !createPostCapability.managed || isAccessible(createPostCapability);
  const [statusDropdownOpen, setStatusDropdownOpen] = useState(false);
  const statusDropdownRef = useRef(null);

  const navigateDesktop = useCallback((href) => {
    if (!href) return;
    if (onNavigate) {
      onNavigate(href);
      return;
    }
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', href);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [onNavigate]);

  const buildStrategizerDnaPath = useCallback((dnaGate) => {
    const params = new URLSearchParams();
    params.set('view', 'dna');
    const companyId = currentCompany?.id ||
      currentCompany?._id ||
      currentCompany?.company_id ||
      currentCompany?.company?.id ||
      currentCompany?.company?._id ||
      currentCompany?.company?.company_id;
    if (companyId) {
      params.set('companyId', companyId);
    }
    if (dnaGate) {
      params.set('dnaGate', dnaGate);
    }
    return `/strategizer?${params.toString()}`;
  }, [currentCompany]);

  useEffect(() => {
    if (companyLoading) return;
    if (!currentCompany) return;
    if (hasCompanyDna(currentCompany)) return;
    if (dnaGateRef.current) return;
    dnaGateRef.current = true;

    const target = buildStrategizerDnaPath('strategy');
    if (typeof onNavigate === 'function') {
      onNavigate(target);
      return;
    }
    if (typeof window !== 'undefined') {
      window.history.pushState({}, '', target);
      window.dispatchEvent(new Event('popstate'));
    }
  }, [companyLoading, currentCompany, onNavigate]);

  const handleCreateNewCampaign = useCallback(() => {
    if (currentCompany && !hasCompanyDna(currentCompany)) {
      notify({
        variant: 'warning',
        title: t('newCampaign.alerts.dnaRequired', {
          defaultValue: 'Prima di creare una strategia crea il tuo DNA.'
        })
      });
      navigateDesktop(buildStrategizerDnaPath('strategy'));
      return;
    }
    navigateDesktop('/desktop/new-strategy');
  }, [buildStrategizerDnaPath, currentCompany, navigateDesktop, notify, t]);

  const resolvedTargetCampaignId = useMemo(() => {
    const direct = normalizeIdValue(targetCampaignId);
    if (direct) return direct;

    try {
      const search = queryString || (typeof window !== 'undefined' ? window.location.search : '');
      const params = new URLSearchParams(search || '');
      const fromQuery = params.get('campaignId') || params.get('targetCampaignId');
      return normalizeIdValue(fromQuery);
    } catch (error) {
      console.warn('[DesktopStrategyPage] Unable to resolve target campaign from query', error);
      return null;
    }
  }, [queryString, targetCampaignId]);

  const weekDateFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (error) {
      console.warn('[DesktopStrategyPage] Using fallback week date formatter', error);
      return new Intl.DateTimeFormat('it', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    }
  }, [language]);

  const formatWeekDate = useCallback((date) => {
    if (!date) return null;
    try {
      const parsed = new Date(date);
      if (Number.isNaN(parsed.getTime())) {
        return null;
      }
      return weekDateFormatter.format(parsed);
    } catch (error) {
      console.warn('[DesktopStrategyPage] Unable to format week date', { date, error });
      return null;
    }
  }, [weekDateFormatter]);

  const weekDayLabelFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      });
    } catch (error) {
      console.warn('[DesktopStrategyPage] Using fallback day formatter', error);
      return new Intl.DateTimeFormat('it', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit'
      });
    }
  }, [language]);

  const timeLabelFormatter = useMemo(() => {
    try {
      return new Intl.DateTimeFormat(language || 'it', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    } catch (error) {
      console.warn('[DesktopStrategyPage] Using fallback time formatter', error);
      return new Intl.DateTimeFormat('it', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    }
  }, [language]);

  const formatDayLabel = useCallback((dateValue) => {
    if (!dateValue) {
      return t('strategy.posts.noDate', { defaultValue: 'Data da definire' });
    }
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return t('strategy.posts.noDate', { defaultValue: 'Data da definire' });
    }
    return weekDayLabelFormatter.format(date);
  }, [t, weekDayLabelFormatter]);

  const formatTimeLabel = useCallback((dateValue) => {
    if (!dateValue) {
      return t('strategy.posts.noTime', { defaultValue: 'Orario da definire' });
    }
    const date = dateValue instanceof Date ? dateValue : new Date(dateValue);
    if (Number.isNaN(date.getTime())) {
      return t('strategy.posts.noTime', { defaultValue: 'Orario da definire' });
    }
    return timeLabelFormatter.format(date);
  }, [timeLabelFormatter, t]);

  const statusLabels = useMemo(() => ({
    draft: t('strategy.status.options.draft', { defaultValue: 'Bozza' }),
    active: t('strategy.status.options.active', { defaultValue: 'Attiva' }),
    paused: t('strategy.status.options.paused', { defaultValue: 'In pausa' }),
    completed: t('strategy.status.options.completed', { defaultValue: 'Completata' })
  }), [t]);

  const getStatusLabel = useCallback((status) => {
    if (status && statusLabels[status]) {
      return statusLabels[status];
    }
    return statusLabels.draft;
  }, [statusLabels]);

  const statusOptions = useMemo(() => STATUS_FALLBACKS.map((value) => ({
    value,
    label: statusLabels[value]
  })), [statusLabels]);

  const getNormalizedPostId = useCallback((post) => {
    if (!post || typeof post !== 'object') return null;
    const directId = post.id ?? post._id ?? post.post_id ?? post.postId ?? post.uuid;
    if (directId !== undefined && directId !== null) {
      return String(directId);
    }

    const nestedId = post?.metadata?.id ?? post?.metadata?.post_id ?? post?.metadata?.postId;
    if (nestedId !== undefined && nestedId !== null) {
      return String(nestedId);
    }

    return null;
  }, []);

  const getPostWeekIndex = useCallback((post) => {
    if (!post || typeof post !== 'object') return 0;

    const candidates = [
      post.week_index,
      post.weekIndex,
      post.week_number,
      post.weekNumber,
      post.strategy_week,
      post.strategyWeek,
      post.campaign_week_index,
      post.campaignWeekIndex,
      post?.metadata?.week_index,
      post?.metadata?.weekIndex,
      post?.schedule?.week_index,
      post?.schedule?.weekIndex
    ];

    for (const candidate of candidates) {
      if (candidate === undefined || candidate === null) continue;

      if (typeof candidate === 'object' && '$numberInt' in candidate) {
        const parsedNumberInt = Number(candidate.$numberInt);
        if (Number.isFinite(parsedNumberInt)) {
          return parsedNumberInt;
        }
        continue;
      }

      const parsed = typeof candidate === 'string' ? Number.parseInt(candidate, 10) : Number(candidate);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }

    return 0;
  }, []);

  const loadCampaignStrategy = useCallback(async (campaignId) => {
    if (!campaignId) return;
    try {
      await loadStrategyFromCampaign(campaignId);
    } catch (error) {
      console.error('[DesktopStrategyPage] Error loading strategy:', error);
    }
  }, [loadStrategyFromCampaign]);

  const fetchCampaignPosts = useCallback(async (campaignId) => {
    if (!campaignId) {
      setCampaignPosts([]);
      setPostsError(null);
      return;
    }

    setPostsLoading(true);
    setPostsError(null);

    try {
      const query = new URLSearchParams({
        campaign_id: campaignId,
        limit: '1000',
        offset: '0'
      });
      const response = await apiGet(`${API_CONFIG.ENDPOINTS.POSTS.LIST}?${query.toString()}`);
      const posts = Array.isArray(response?.posts) ? response.posts : [];
      const normalizedPosts = posts.map((post) => {
        const normalizedId = getNormalizedPostId(post);
        return normalizedId ? { ...post, id: normalizedId } : post;
      });
      setCampaignPosts(normalizedPosts);
    } catch (error) {
      console.error('[DesktopStrategyPage] Failed to load campaign posts:', error);
      setCampaignPosts([]);
      setPostsError(error?.message || t('strategy.errors.postsLoad', { defaultValue: 'Errore nel caricamento dei post' }));
    } finally {
      setPostsLoading(false);
    }
  }, [apiGet, getNormalizedPostId, t]);

  const isBootstrappingCompany = companyLoading && !currentCompany;
  const isDnaMissing = Boolean(currentCompany) && !hasCompanyDna(currentCompany);
  const shouldHoldForGate = isBootstrappingCompany || isDnaMissing;

  useEffect(() => {
    if (shouldHoldForGate) return;
    if (currentCampaign?.id) {
      loadCampaignStrategy(currentCampaign.id);
      fetchCampaignPosts(currentCampaign.id);
    }
  }, [currentCampaign?.id, fetchCampaignPosts, loadCampaignStrategy, postsRefreshKey, shouldHoldForGate]);

  useEffect(() => {
    if (shouldHoldForGate) return;
    if (resolvedTargetCampaignId && campaigns.length > 0) {
      const targetCampaign = campaigns.find(
        (campaign) => normalizeIdValue(campaign?.id ?? campaign?._id) === resolvedTargetCampaignId
      );
      if (targetCampaign) {
        switchCampaign(targetCampaign);
      }
    }
  }, [campaigns, resolvedTargetCampaignId, shouldHoldForGate, switchCampaign]);

  useEffect(() => {
    setSelectedWeek(0);
  }, [strategyData]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const strategyWeeks = Array.isArray(strategyData?.weeks) ? strategyData.weeks : [];
  const strategyWeeksCount = strategyWeeks.length;
  const extraWeekIndex = strategyWeeksCount;

  const lastWeekEndDate = useMemo(() => {
    if (!strategyWeeksCount) return null;
    const lastWeek = strategyWeeks[strategyWeeksCount - 1];
    const rawEndDate = lastWeek?.end_date || lastWeek?.endDate;
    if (!rawEndDate) return null;
    const parsed = new Date(rawEndDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [strategyWeeks, strategyWeeksCount]);

  const isExtraWeekSelected = strategyWeeksCount > 0 && selectedWeek === extraWeekIndex;

  const postsById = useMemo(() => {
    const map = new Map();
    campaignPosts.forEach((post) => {
      const postId = getNormalizedPostId(post);
      if (postId) {
        map.set(postId, post);
      }
    });
    return map;
  }, [campaignPosts, getNormalizedPostId]);

  const postsByWeek = useMemo(() => {
    const map = new Map();
    campaignPosts.forEach((post) => {
      const scheduledDate = getPostScheduledDate(post);
      let weekIndex = getPostWeekIndex(post);
      const hasValidWeekIndex = Number.isFinite(weekIndex) && weekIndex >= 0;
      const isBeyondGeneratedWeeks = strategyWeeksCount > 0 && hasValidWeekIndex && weekIndex >= strategyWeeksCount;
      const isBeyondByDate = strategyWeeksCount > 0 && lastWeekEndDate && scheduledDate && scheduledDate > lastWeekEndDate;

      if (isBeyondGeneratedWeeks || isBeyondByDate) {
        weekIndex = extraWeekIndex;
      } else if (!hasValidWeekIndex) {
        weekIndex = 0;
      }

      const normalizedWeekIndex = Number.isFinite(weekIndex) && weekIndex >= 0 ? weekIndex : 0;

      if (!map.has(normalizedWeekIndex)) {
        map.set(normalizedWeekIndex, []);
      }
      map.get(normalizedWeekIndex).push(post);
    });
    return map;
  }, [campaignPosts, extraWeekIndex, getPostWeekIndex, lastWeekEndDate, strategyWeeksCount]);

  const weekPosts = useMemo(() => {
    const strategyWeekPosts = strategyWeeks[selectedWeek]?.posts || [];
    const fallbackWeekPosts = postsByWeek.get(selectedWeek) || [];

    if (!Array.isArray(strategyWeekPosts) || strategyWeekPosts.length === 0) {
      return fallbackWeekPosts;
    }

    const normalizeMediaArray = (value) => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return [value];
    };

    const mergedPosts = strategyWeekPosts.map((post) => {
      const postId = getNormalizedPostId(post);
      const apiPost = postId ? postsById.get(postId) : null;

      if (!apiPost) {
        return post;
      }

      const mediaFromApi = normalizeMediaArray(apiPost.media_urls || apiPost.images || apiPost.mediaUrls);
      const mediaFromStrategy = normalizeMediaArray(post.media_urls || post.images || post.mediaUrls);
      const mergedMedia = mediaFromApi.length > 0 ? mediaFromApi : mediaFromStrategy;

      return {
        ...post,
        ...apiPost,
        id: postId,
        media_urls: mergedMedia,
        images: mergedMedia,
        versions: Array.isArray(apiPost.versions) && apiPost.versions.length > 0 ? apiPost.versions : post.versions,
        current_version: apiPost.current_version || apiPost.currentVersion || post.current_version,
        current_version_id: apiPost.current_version_id || apiPost.currentVersionId || post.current_version_id
      };
    });

    const knownIds = new Set(mergedPosts.map((post) => getNormalizedPostId(post)).filter(Boolean));
    fallbackWeekPosts.forEach((post) => {
      const postId = getNormalizedPostId(post);
      if (postId && !knownIds.has(postId)) {
        mergedPosts.push(post);
      }
    });

    return mergedPosts;
  }, [getNormalizedPostId, postsById, postsByWeek, selectedWeek, strategyWeeks]);

  const selectedWeekData = useMemo(() => {
    if (!strategyWeeks.length) {
      return null;
    }
    return strategyWeeks[selectedWeek] || null;
  }, [selectedWeek, strategyWeeks]);

  const selectedWeekStrategy = useMemo(() => {
    if (!selectedWeekData) return '';
    const candidates = [
      selectedWeekData.strategy,
      selectedWeekData.week_strategy,
      selectedWeekData.headline,
      selectedWeekData.theme,
      selectedWeekData.focus,
      selectedWeekData.summary,
      selectedWeekData.desc
    ];
    return candidates.find((value) => typeof value === 'string' && value.trim())?.trim() || '';
  }, [selectedWeekData]);

  const selectedWeekPeriod = useMemo(() => {
    if (isExtraWeekSelected) {
      const formattedStart = lastWeekEndDate ? formatWeekDate(lastWeekEndDate) : null;
      if (formattedStart) {
        return t('strategy.week.extraPeriod', { start: formattedStart, defaultValue: `Dal ${formattedStart} in poi` });
      }
      return t('strategy.week.extraLabel', { defaultValue: 'Settimana extra' });
    }

    const startDate = selectedWeekData?.start_date ? formatWeekDate(selectedWeekData.start_date) : null;
    const endDate = selectedWeekData?.end_date ? formatWeekDate(selectedWeekData.end_date) : null;

    if (startDate && endDate) {
      return t('strategy.week.period', { start: startDate, end: endDate, defaultValue: `${startDate} → ${endDate}` });
    }

    return t('strategy.week.datePlaceholder', { defaultValue: 'Periodo non disponibile' });
  }, [formatWeekDate, isExtraWeekSelected, lastWeekEndDate, selectedWeekData, t]);

  const weekSteps = useMemo(() => {
    if (!strategyWeeks.length) {
      return [];
    }

    const buildWeekTitle = (label, number) => {
      const raw = (label ?? '').toString().trim();
      if (!raw) return `Settimana ${number}`;
      const normalized = raw.toLowerCase();
      if (normalized.includes('settimana')) return raw;
      if (/^\d+$/.test(raw)) {
        return `Settimana ${raw}`;
      }
      return `Settimana ${number}`;
    };

    const steps = strategyWeeks.map((_, index) => {
      const translated = t('strategy.week.short', {
        number: index + 1,
        defaultValue: `Settimana ${index + 1}`
      });

      return {
        step: index + 1,
        title: buildWeekTitle(translated, index + 1)
      };
    });

    steps.push({
      step: strategyWeeks.length + 1,
      title: t('strategy.week.extra', { defaultValue: 'Extra' }) || 'Extra',
      isExtra: true
    });

    return steps;
  }, [strategyWeeks, t]);

  const weekCount = useMemo(() => {
    if (strategyWeeksCount > 0) {
      return strategyWeeksCount + 1;
    }
    return postsByWeek.size;
  }, [postsByWeek, strategyWeeksCount]);

  const totalPostsCount = useMemo(() => {
    if (strategyWeeksCount > 0) {
      return strategyWeeks.reduce(
        (total, week) => total + (Array.isArray(week?.posts) ? week.posts.length : 0),
        0
      );
    }
    return campaignPosts.length;
  }, [campaignPosts, strategyWeeks, strategyWeeksCount]);

  const handleCampaignSelect = useCallback((campaign) => {
    switchCampaign(campaign);
  }, [switchCampaign]);

  const handleStatusChange = useCallback(async (newStatus) => {
    if (!currentCampaign?.id) return;
    try {
      setStatusDropdownOpen(false);
      if (updateCampaign) {
        await updateCampaign(currentCampaign.id, { status: newStatus });
      } else {
        const updatedCampaign = { ...currentCampaign, status: newStatus };
        switchCampaign(updatedCampaign);
      }
    } catch (error) {
      console.error('[DesktopStrategyPage] Error updating campaign status:', error);
    }
  }, [currentCampaign, switchCampaign, updateCampaign]);

  const handleGenerateStrategy = useCallback(async () => {
    if (!currentCampaign?.id) return;
    try {
      const strategyOptions = {
        user_prompt: 'Generate a comprehensive strategy based on the campaign parameters',
        include_company_analysis: true
      };
      await generateStrategy(currentCampaign.id, strategyOptions);
      await loadCampaignStrategy(currentCampaign.id);
      setPostsRefreshKey((prev) => prev + 1);
    } catch (error) {
      console.error('[DesktopStrategyPage] Error generating strategy:', error);
    }
  }, [currentCampaign?.id, generateStrategy, loadCampaignStrategy]);

  const handleStartPostCreation = useCallback(() => {
    if (!canStartPostCreation) return;
    if (!currentCampaign?.id) return;

    const params = new URLSearchParams();
    params.set('tab', 'post');

    const campaignId = normalizeIdValue(currentCampaign?.id ?? currentCampaign?._id ?? currentCampaign?.value);
    if (campaignId) {
      params.set('campaignId', campaignId);
    }

    const strategyId = normalizeIdValue(
      currentCampaign?.strategy?.id ??
      currentCampaign?.strategy?._id ??
      currentCampaign?.strategy?.value ??
      currentCampaign?.strategy_id ??
      currentCampaign?.strategyId
    );
    if (strategyId) {
      params.set('strategyId', strategyId);
    }

    params.set('useCompanyDna', '1');

    const query = params.toString();
    navigateDesktop(query ? `/desktop/create-post?${query}` : '/desktop/create-post');
  }, [canStartPostCreation, currentCampaign, navigateDesktop]);

  const selectWeek = useCallback((weekIndex) => {
    setSelectedWeek(weekIndex);
  }, []);

  const openImagePreview = useCallback((img, gallery = []) => {
    const imageUrl = img?.file_url || img?.url || img?.src || null;
    setImagePreview(imageUrl);
    if (gallery.length > 0) {
      setImageGallery(gallery);
      const currentIndex = gallery.findIndex((item) => {
        const itemUrl = item?.file_url || item?.url || item?.src;
        return itemUrl === imageUrl;
      });
      setCurrentImageIndex(currentIndex >= 0 ? currentIndex : 0);
    } else {
      setImageGallery([img]);
      setCurrentImageIndex(0);
    }
  }, []);

  const closeImagePreview = useCallback(() => {
    setImagePreview(null);
    setImageGallery([]);
    setCurrentImageIndex(0);
  }, []);

  const nextImage = useCallback(() => {
    if (imageGallery.length > 0) {
      const nextIndex = (currentImageIndex + 1) % imageGallery.length;
      setCurrentImageIndex(nextIndex);
      const nextImg = imageGallery[nextIndex];
      setImagePreview(nextImg?.file_url || nextImg?.url || nextImg?.src || null);
    }
  }, [currentImageIndex, imageGallery]);

  const prevImage = useCallback(() => {
    if (imageGallery.length > 0) {
      const prevIndex = (currentImageIndex - 1 + imageGallery.length) % imageGallery.length;
      setCurrentImageIndex(prevIndex);
      const prevImg = imageGallery[prevIndex];
      setImagePreview(prevImg?.file_url || prevImg?.url || prevImg?.src || null);
    }
  }, [currentImageIndex, imageGallery]);

  const shouldShowStrategySkeleton = (
    (campaignLoading || strategyLoading || isGeneratingStrategy) &&
    !campaignError &&
    !strategyError
  );

  const renderSkeleton = () => (
    <div className="loopino-strategy-panel">
      <div className="loopino-strategy-panel-header">
        <div className="loopino-strategy-heading">
          <Skeleton width="110px" height={12} shimmer />
          <Skeleton width="340px" height={34} shimmer style={{ marginTop: 8 }} />
          <Skeleton width="460px" height={14} shimmer style={{ marginTop: 10 }} />
          <Skeleton width="360px" height={14} shimmer />
        </div>
        <div className="loopino-strategy-actions">
          <Skeleton width="164px" height={42} shimmer />
          <Skeleton width="186px" height={42} shimmer />
        </div>
      </div>
      <div className="loopino-strategy-meta-grid">
        <div className="loopino-strategy-meta-card">
          <Skeleton width="122px" height={10} shimmer />
          <Skeleton width="66px" height={28} shimmer style={{ marginTop: 10 }} />
          <Skeleton width="180px" height={12} shimmer style={{ marginTop: 10 }} />
        </div>
        <div className="loopino-strategy-meta-card">
          <Skeleton width="96px" height={10} shimmer />
          <Skeleton width="72px" height={28} shimmer />
          <Skeleton width="210px" height={12} shimmer style={{ marginTop: 10 }} />
        </div>
        <div className="loopino-strategy-meta-card">
          <Skeleton width="128px" height={10} shimmer />
          <Skeleton width="66px" height={28} shimmer style={{ marginTop: 10 }} />
          <Skeleton width="190px" height={12} shimmer style={{ marginTop: 10 }} />
        </div>
      </div>
      <div className="loopino-strategy-grid">
        <div className="loopino-strategy-card">
          <div className="loopino-strategy-card-head">
            <Skeleton width="96px" height={10} shimmer />
            <Skeleton width="220px" height={20} shimmer style={{ marginTop: 6 }} />
          </div>
          <Skeleton width="94%" height={13} shimmer />
          <Skeleton width="88%" height={13} shimmer />
        </div>
        <div className="loopino-strategy-card">
          <div className="loopino-strategy-card-head">
            <Skeleton width="130px" height={10} shimmer />
            <Skeleton width="210px" height={20} shimmer style={{ marginTop: 6 }} />
          </div>
          <div className="loopino-strategy-style-grid">
            {Array.from({ length: 6 }).map((_, idx) => (
              <Skeleton key={`style-skeleton-${idx}`} variant="rounded" width="100%" height={118} shimmer />
            ))}
          </div>
        </div>
        <div className="loopino-strategy-card">
          <div className="loopino-strategy-card-head">
            <Skeleton width="140px" height={10} shimmer />
            <Skeleton width="180px" height={20} shimmer style={{ marginTop: 6 }} />
          </div>
          <div className="loopino-strategy-asset-list">
            {Array.from({ length: 2 }).map((_, idx) => (
              <div key={`pdf-skeleton-${idx}`} className="loopino-strategy-asset-row">
                <div style={{ flex: 1 }}>
                  <Skeleton width="74%" height={13} shimmer />
                  <Skeleton width="52%" height={11} shimmer style={{ marginTop: 8 }} />
                </div>
                <Skeleton width="82px" height={32} shimmer />
              </div>
            ))}
          </div>
        </div>
        <div className="loopino-strategy-card">
          <div className="loopino-strategy-card-head">
            <Skeleton width="130px" height={10} shimmer />
            <Skeleton width="190px" height={20} shimmer style={{ marginTop: 6 }} />
          </div>
          <div className="loopino-strategy-style-grid">
            {Array.from({ length: 4 }).map((_, idx) => (
              <Skeleton key={`image-skeleton-${idx}`} variant="rounded" width="100%" height={110} shimmer />
            ))}
          </div>
        </div>
      </div>
      <div className="loopino-strategy-weeks">
        <div className="loopino-week-stepper">
          <div className="loopino-week-stepper-inner">
            {Array.from({ length: 3 }).map((_, idx) => (
              <div key={`week-tab-${idx}`} className="loopino-stepper-trigger">
                <Skeleton width="100%" height={6} shimmer />
                <div className="loopino-stepper-copy">
                  <Skeleton width="72%" height={14} shimmer />
                  <Skeleton width="46%" height={12} shimmer />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="loopino-strategy-week-card">
          <div className="loopino-strategy-week-head">
            <div>
              <Skeleton width="180px" height={14} shimmer />
              <Skeleton width="420px" height={12} shimmer style={{ marginTop: 10 }} />
            </div>
            <div className="loopino-strategy-inline-actions">
              <Skeleton width="164px" height={36} shimmer />
              <Skeleton width="190px" height={36} shimmer />
            </div>
          </div>
          <div className="loopino-strategy-week-highlight">
            <Skeleton width="160px" height={14} shimmer />
            <Skeleton width="90%" height={14} shimmer />
            <Skeleton width="78%" height={14} shimmer />
          </div>
          <div className="loopino-strategy-posts">
            <div className="loopino-strategy-posts-head">
              <Skeleton width="180px" height={16} shimmer />
              <Skeleton width="100px" height={12} shimmer />
            </div>
            <div className="loopino-strategy-week-rail">
              <DesktopHorizontalScroll trackClassName="loopino-strategy-week-track" padding="8px">
                {Array.from({ length: 4 }).map((_, idx) => (
                  <article key={`post-${idx}`} className="loopino-strategy-week-post-card">
                    <div className="loopino-strategy-week-post-meta">
                      <Skeleton width="48px" height={10} shimmer />
                      <Skeleton width="44px" height={10} shimmer />
                      <Skeleton width="40px" height={10} shimmer />
                    </div>
                    <div className="loopino-strategy-week-post-body">
                      <Skeleton variant="rounded" width="100%" height={148} shimmer />
                      <Skeleton width="92%" height={12} shimmer />
                      <Skeleton width="54%" height={12} shimmer />
                    </div>
                    <div className="loopino-strategy-week-footer">
                      <Skeleton width="88px" height={12} shimmer />
                      <Skeleton width="66px" height={12} shimmer />
                    </div>
                  </article>
                ))}
              </DesktopHorizontalScroll>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const renderGateSkeleton = () => (
    <div className="loopino-desktop-shell loopino-desktop-strategy-shell">
      <header className="loopino-desktop-post-review-header loopino-desktop-strategy-header">
        <div>
          <Skeleton width="140px" height={12} shimmer />
          <Skeleton width="300px" height={34} shimmer style={{ marginTop: 8 }} />
          <Skeleton width="520px" height={14} shimmer style={{ marginTop: 10 }} />
        </div>
        <div className="loopino-calendar-header-actions">
          <Skeleton width="190px" height={40} shimmer />
        </div>
      </header>
      {renderSkeleton()}
    </div>
  );

  const renderAssetList = (files = [], variant = 'pdf') => {
    if (!files.length) {
      return (
        <p className="loopino-strategy-empty-hint">
          {variant === 'pdf'
            ? t('strategy.section.media.pdfEmpty', { defaultValue: 'Nessun documento caricato' })
            : t('strategy.section.media.imagesEmpty', { defaultValue: 'Nessuna immagine disponibile' })}
        </p>
      );
    }

    if (variant === 'pdf') {
      return (
        <ul className="loopino-strategy-asset-list">
          {files.map((file, idx) => (
            <li key={`${file.filename || file.name || idx}-${idx}`} className="loopino-strategy-asset-row">
              <div className="loopino-strategy-asset-info">
                <span className="loopino-strategy-asset-name">{file.filename || file.name || t('strategy.section.media.pdfFallback', { defaultValue: 'Documento' })}</span>
                <span className="loopino-strategy-asset-meta">{file.mime_type || file.file_type || 'PDF'}</span>
              </div>
              {file.url || file.file_url ? (
                <a
                  className="loopino-strategy-asset-action"
                  href={file.url || file.file_url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t('strategy.section.media.actions.open', { defaultValue: 'Apri' })}
                </a>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }

    return (
      <div className="loopino-strategy-style-grid">
        {files.map((file, idx) => (
          <button
            type="button"
            key={`${file.filename || file.url || idx}-${idx}`}
            className="loopino-strategy-style-thumb"
            onClick={() => openImagePreview({ url: file.url, file_url: file.file_url }, files)}
          >
            <img src={file.url || file.file_url} alt={file.filename || t('strategy.section.media.imageFallback', { defaultValue: 'Immagine' })} />
          </button>
        ))}
      </div>
    );
  };

  const renderWeekPostCard = (post) => {
    const cover = getFirstMediaUrl(post);
    const title = post?.content || post?.caption || post?.title || t('strategy.posts.untitled', { defaultValue: 'Post senza titolo' });
    const profileName = getPostProfileName(post);
    const statusInfo = getWeekPostStatusInfo(post);
    const scheduledDate = getPostScheduledDate(post);
    const postId = getNormalizedPostId(post);
    const isVideo = isVideoFile(cover);
    const platform = (post?.platform || post?.social_platform || '').toString().toLowerCase();

    return (
      <article key={postId || title} className={`loopino-strategy-week-post-card ${statusInfo.cardClass}`}>
        <div className="loopino-strategy-week-post-meta">
          <div className="loopino-strategy-week-meta-left">
            <span className={`loopino-strategy-week-status status-${statusInfo.badgeVariant}`} />
            <span className="loopino-strategy-week-time">{formatTimeLabel(scheduledDate)}</span>
          </div>
          <span className="loopino-strategy-week-day">{formatDayLabel(scheduledDate)}</span>
        </div>

        <div className="loopino-strategy-week-post-body">
          {cover ? (
            <div className="loopino-strategy-week-thumb">
              <img src={cover} alt={title} />
              {isVideo && (
                <div className="loopino-strategy-week-thumb-badge" aria-hidden="true">
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <polygon points="5,3 19,12 5,21" fill="white" stroke="white" strokeWidth="2" />
                  </svg>
                </div>
              )}
            </div>
          ) : (
            <div className="loopino-strategy-week-thumb placeholder">
              <span>{t('strategy.posts.noImage', { defaultValue: 'Anteprima non disponibile' })}</span>
            </div>
          )}

          <p className="loopino-strategy-week-title">{title}</p>
        </div>

        <div className="loopino-strategy-week-footer">
          <div className="loopino-strategy-week-meta-left">
            <PlatformIcon platform={platform} size={14} />
            {profileName && <span className="loopino-strategy-week-profile">{profileName}</span>}
          </div>
          {post.campaignName && <span className="loopino-strategy-week-campaign">{post.campaignName}</span>}
        </div>
      </article>
    );
  };

  const renderWeekSection = () => {
    if (!weekSteps.length) {
      return (
        <div className="loopino-strategy-card">
          <h4>{t('strategy.section.weeks.emptyTitle', { defaultValue: 'Nessuna settimana disponibile' })}</h4>
          <p className="loopino-text-muted">
            {t('strategy.section.weeks.emptyBody', { defaultValue: 'Genera la strategia per visualizzare il piano settimanale.' })}
          </p>
          <div className="loopino-strategy-inline-actions">
            <button type="button" className="loopino-calendar-solid-button" onClick={handleGenerateStrategy} disabled={isGeneratingStrategy}>
              {t('strategy.actions.generate', { defaultValue: 'Genera strategia' })}
            </button>
          </div>
        </div>
      );
    }

    const weekDescription = (selectedWeekData?.desc && selectedWeekData.desc.trim()) || '';
    const showSecondaryDescription = weekDescription && weekDescription !== selectedWeekStrategy;

    return (
      <div className="loopino-strategy-weeks">
        <div className="loopino-week-stepper" role="tablist" aria-label={t('strategy.week.selectorLabel', { defaultValue: 'Selettore di settimana' })}>
          <div className="loopino-week-stepper-inner">
            {weekSteps.map(({ step, title }, index) => {
              const isActive = selectedWeek === index;
              const isPast = index < selectedWeek;
              const composedLabel = title;

              return (
                <button
                  key={`week-${step}`}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  aria-label={composedLabel}
                  title={composedLabel}
                  className={`loopino-stepper-trigger ${isActive ? 'is-active' : ''} ${isPast ? 'is-past' : ''}`}
                  onClick={() => selectWeek(index)}
                >
                  <span className="loopino-stepper-indicator" aria-hidden="true">
                    <span className="sr-only">{title}</span>
                  </span>
                  <div className="loopino-stepper-copy">
                    <span className="loopino-stepper-title">{title}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="loopino-strategy-week-card">
          <div className="loopino-strategy-week-head">
            <div>
              <p className="loopino-strategy-week-period">{selectedWeekPeriod}</p>
              {isExtraWeekSelected ? (
                weekDescription ? <p className="loopino-text-muted">{weekDescription}</p> : null
              ) : showSecondaryDescription ? (
                <p className="loopino-text-muted">{weekDescription}</p>
              ) : (
                <p className="loopino-text-muted">
                  {selectedWeekStrategy
                    ? t('strategy.week.highlightHint', { defaultValue: 'Focus della settimana evidenziato qui sotto.' })
                    : t('strategy.week.noDescription', { defaultValue: 'Nessuna descrizione per questa settimana' })}
                </p>
              )}
            </div>
            <div className="loopino-strategy-inline-actions">
              {!isExtraWeekSelected && (
                <>
                  <button
                    type="button"
                    className="loopino-calendar-solid-button"
                    onClick={handleStartPostCreation}
                    disabled={!canStartPostCreation}
                  >
                    {t('strategy.actions.createPosts', { defaultValue: 'Avvia creazione post' })}
                  </button>
                </>
              )}
            </div>
          </div>
          {!isExtraWeekSelected && (
            <div className="loopino-strategy-week-highlight">
              <span className="loopino-strategy-week-chip">
                {t('strategy.week.strategyLabel', { defaultValue: 'Strategia della settimana' })}
              </span>
              <p className="loopino-strategy-week-strategy">
                {selectedWeekStrategy || t('strategy.week.noStrategy', { defaultValue: 'Aggiungi la strategia settimanale per guidare la creazione dei post.' })}
              </p>
            </div>
          )}
          <div className="loopino-strategy-posts">
            <div className="loopino-strategy-posts-head">
              <h5>{t('strategy.posts.header', { count: Array.isArray(weekPosts) ? weekPosts.length : 0, defaultValue: 'Post della settimana' })}</h5>
              {postsLoading && <span className="loopino-text-muted">{t('common.loading', { defaultValue: 'Caricamento...' })}</span>}
            </div>
            {postsError && (
              <div className="loopino-strategy-inline-alert">
                <span>{postsError}</span>
                <button type="button" className="loopino-calendar-ghost-button" onClick={() => fetchCampaignPosts(currentCampaign?.id)}>
                  {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
                </button>
              </div>
            )}
            {Array.isArray(weekPosts) && weekPosts.length > 0 ? (
              <div className="loopino-strategy-week-rail">
                <DesktopHorizontalScroll trackClassName="loopino-strategy-week-track" padding="8px">
                  {weekPosts.map((post) => renderWeekPostCard(post))}
                </DesktopHorizontalScroll>
              </div>
            ) : (
              <div className="loopino-strategy-empty">
                <p>{t('strategy.posts.empty', { defaultValue: 'Nessun post disponibile per questa settimana' })}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (shouldHoldForGate) {
    return renderGateSkeleton();
  }

  if (!currentCampaign && !campaignLoading) {
    const hasNoCampaigns = campaigns.length === 0;

    return (
      <div className="loopino-desktop-shell loopino-desktop-strategy-shell">
        <header className="loopino-desktop-post-review-header loopino-desktop-strategy-header">
          <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('strategy.desktop.title', { defaultValue: 'Strategia campagne' })}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('strategy.desktop.subtitle', { defaultValue: 'Seleziona una campagna per consultare e rigenerare la strategia AI.' })}
          </p>
        </div>
        <div className="loopino-calendar-header-actions">
          <button
            type="button"
            className="loopino-button loopino-strategy-create-button"
            data-variant="primary"
            onClick={handleCreateNewCampaign}
          >
            <img src={iconAi} alt="" />
            {t('strategy.actions.createCampaign', { defaultValue: 'Crea nuova campagna' })}
          </button>
        </div>
      </header>

        <DesktopCampaignSelector
          campaigns={campaigns}
          selectionMode="single"
          activeCampaign={currentCampaign}
          onCampaignSelect={handleCampaignSelect}
          requireConfirmation={false}
          title={t('strategy.desktop.selectorTitle', { defaultValue: 'Scegli la campagna' })}
          eyebrow={t('strategy.desktop.selectorEyebrow', { defaultValue: 'Campagne' })}
          description={t('strategy.desktop.selectorDescription', { defaultValue: 'La selezione influenza i contenuti e le settimane generate.' })}
        />

        <div className="loopino-strategy-panel">
          <div className="loopino-strategy-empty">
            {hasNoCampaigns ? (
              <>
                <p>{t('strategy.empty.title', { defaultValue: 'Nessuna campagna ancora.' })}</p>
                <p>{t('strategy.empty.description', { defaultValue: 'Crea la tua prima campagna per iniziare a generare la strategia.' })}</p>
                <div className="loopino-strategy-empty-actions">
                  <button
                    type="button"
                    className="loopino-button"
                    data-variant="primary"
                    onClick={handleCreateNewCampaign}
                  >
                    {t('strategy.empty.cta', { defaultValue: 'Crea la tua prima campagna' })}
                  </button>
                </div>
              </>
            ) : (
              <p>{t('strategy.prompt.description', { defaultValue: 'Nessuna campagna selezionata. Usa la strip sopra per iniziare.' })}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="loopino-desktop-shell loopino-desktop-strategy-shell">
      <header className="loopino-desktop-post-review-header loopino-desktop-strategy-header">
        <div>
          <p className="loopino-desktop-post-review-eyebrow">
            {t('desktop.common.eyebrow', { defaultValue: 'Loopino desktop' })}
          </p>
          <h1>{t('strategy.desktop.title', { defaultValue: 'Strategia campagne' })}</h1>
          <p className="loopino-desktop-post-review-summary">
            {t('strategy.desktop.subtitle', {
              defaultValue: 'Gestisci piani settimanali, assets e stato delle campagne con lo stesso flusso mobile.'
            })}
          </p>
        </div>
        <div className="loopino-calendar-header-actions">
          <button
            type="button"
            className="loopino-button loopino-strategy-create-button"
            data-variant="primary"
            onClick={handleCreateNewCampaign}
          >
            <img src={iconAi} alt="" />
            {t('strategy.actions.createCampaign', { defaultValue: 'Crea nuova campagna' })}
          </button>
        </div>
      </header>

      <DesktopCampaignSelector
        campaigns={campaigns}
        selectionMode="single"
        activeCampaign={currentCampaign}
        onCampaignSelect={handleCampaignSelect}
        requireConfirmation={false}
        title={t('strategy.desktop.selectorTitle', { defaultValue: 'Scegli la campagna' })}
        eyebrow={t('strategy.desktop.selectorEyebrow', { defaultValue: 'Campagne' })}
        description={t('strategy.desktop.selectorDescription', { defaultValue: 'La selezione influenza i contenuti e le settimane generate.' })}
      />

      {campaignError && (
        <div className="loopino-strategy-panel">
          <div className="loopino-strategy-inline-alert">
            <span>{campaignError}</span>
            <button type="button" className="loopino-calendar-ghost-button" onClick={loadCampaigns}>
              {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
            </button>
          </div>
        </div>
      )}

      {strategyError && (
        <div className="loopino-strategy-panel">
          <div className="loopino-strategy-inline-alert">
            <span>{t('strategy.errors.loadStrategy', { error: String(strategyError), defaultValue: 'Errore nel caricamento della strategia' })}</span>
            <button type="button" className="loopino-calendar-ghost-button" onClick={() => loadCampaignStrategy(currentCampaign?.id)}>
              {t('common.actions.refresh', { defaultValue: 'Aggiorna' })}
            </button>
          </div>
        </div>
      )}

      {shouldShowStrategySkeleton ? (
        renderSkeleton()
      ) : (
        <div className="loopino-strategy-panel">
          <div className="loopino-strategy-panel-header">
            <div className="loopino-strategy-heading">
              <p className="loopino-calendar-eyebrow">{t('strategy.desktop.campaignEyebrow', { defaultValue: 'Campagna' })}</p>
              <h3 className="loopino-strategy-title">{currentCampaign?.name}</h3>
              <p className="loopino-text-muted">{currentCampaign?.description || t('strategy.section.campaignFallback', { defaultValue: 'Nessuna descrizione disponibile' })}</p>
            </div>
            <div className="loopino-strategy-actions" ref={statusDropdownRef}>
              <div className="loopino-strategy-status">
                <button type="button" className="loopino-strategy-status-button" onClick={() => setStatusDropdownOpen((prev) => !prev)}>
                  <span className={`loopino-strategy-status-dot status-${currentCampaign?.status || 'draft'}`} />
                  <span>{getStatusLabel(currentCampaign?.status)}</span>
                  <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </button>
                {statusDropdownOpen && (
                  <div className="loopino-strategy-status-menu">
                    {statusOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        className={`loopino-strategy-status-item ${currentCampaign?.status === option.value ? 'is-active' : ''}`}
                        onClick={() => handleStatusChange(option.value)}
                      >
                        <span className={`loopino-strategy-status-dot status-${option.value}`} />
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                type="button"
                className="loopino-calendar-solid-button"
                onClick={handleStartPostCreation}
                disabled={!canStartPostCreation}
              >
                {t('strategy.actions.createPosts', { defaultValue: 'Avvia creazione post' })}
              </button>
            </div>
          </div>

          <div className="loopino-strategy-meta-grid">
            <div className="loopino-strategy-meta-card">
              <p className="loopino-strategy-meta-label">{t('strategy.desktop.metric.weeks', { defaultValue: 'Settimane generate' })}</p>
              <p className="loopino-strategy-meta-value">{weekCount}</p>
              <p className="loopino-strategy-meta-hint">{t('strategy.desktop.metric.weeksHint', { defaultValue: 'Distribuzione temporale' })}</p>
            </div>
            <div className="loopino-strategy-meta-card">
              <p className="loopino-strategy-meta-label">{t('strategy.desktop.metric.posts', { defaultValue: 'Post stimati' })}</p>
              <p className="loopino-strategy-meta-value">{totalPostsCount}</p>
              <p className="loopino-strategy-meta-hint">{t('strategy.desktop.metric.postsHint', { defaultValue: 'Conteggio stimato sui piani' })}</p>
            </div>
            <div className="loopino-strategy-meta-card">
              <p className="loopino-strategy-meta-label">{t('strategy.desktop.metric.assets', { defaultValue: 'Assets collegati' })}</p>
              <p className="loopino-strategy-meta-value">{(mediaFiles?.pdf?.length || 0) + (mediaFiles?.image?.length || 0)}</p>
              <p className="loopino-strategy-meta-hint">{t('strategy.desktop.metric.assetsHint', { defaultValue: 'Documenti e immagini allegati' })}</p>
            </div>
          </div>

          <div className="loopino-strategy-grid">
            <div className="loopino-strategy-card">
              <div className="loopino-strategy-card-head">
                <p className="loopino-calendar-eyebrow">{t('strategy.section.general.eyebrow', { defaultValue: 'Sintesi' })}</p>
                <h4>{t('strategy.section.general.title', { defaultValue: 'Strategia generale' })}</h4>
              </div>
              <p className="loopino-text-muted">
                {strategyData?.desc || t('strategy.section.general.empty', { defaultValue: 'Nessuna descrizione disponibile per questa strategia.' })}
              </p>
            </div>

            <div className="loopino-strategy-card">
              <div className="loopino-strategy-card-head">
                <p className="loopino-calendar-eyebrow">{t('strategy.section.media.title', { defaultValue: 'Style guide & assets' })}</p>
                <h4>{t('strategy.section.media.styleGuideHeading', { count: strategyImages?.length || 0, defaultValue: 'Moodboard' })}</h4>
              </div>
              {strategyImages?.length ? (
                <div className="loopino-strategy-style-grid">
                  {strategyImages.map((img, idx) => (
                    <button
                      type="button"
                      key={`style-${idx}`}
                      className="loopino-strategy-style-thumb"
                      onClick={() => openImagePreview(img, strategyImages)}
                    >
                      <img src={img.src || img.url || img.file_url} alt={img.image_alt || t('strategy.section.media.alt.styleGuide', { defaultValue: 'Immagine stile' })} />
                    </button>
                  ))}
                </div>
              ) : (
                <p className="loopino-strategy-empty-hint">
                  {t('strategy.section.media.styleGuideEmpty', { defaultValue: 'Aggiungi immagini di riferimento per guidare la creazione.' })}
                </p>
              )}
            </div>

            <div className="loopino-strategy-card">
              <div className="loopino-strategy-card-head">
                <p className="loopino-calendar-eyebrow">{t('strategy.section.media.pdfTitle', { count: mediaFiles?.pdf?.length || 0, defaultValue: 'Documenti' })}</p>
                <h4>{t('strategy.section.media.pdfTitle', { count: mediaFiles?.pdf?.length || 0, defaultValue: 'Documenti' })}</h4>
              </div>
              {renderAssetList(mediaFiles?.pdf || [], 'pdf')}
            </div>

            <div className="loopino-strategy-card">
              <div className="loopino-strategy-card-head">
                <p className="loopino-calendar-eyebrow">{t('strategy.section.media.imagesTitle', { count: mediaFiles?.image?.length || 0, defaultValue: 'Immagini' })}</p>
                <h4>{t('strategy.section.media.imagesTitle', { count: mediaFiles?.image?.length || 0, defaultValue: 'Immagini' })}</h4>
              </div>
              {renderAssetList(mediaFiles?.image || [], 'image')}
            </div>
          </div>

          {renderWeekSection()}
        </div>
      )}

      {imagePreview && (
        <div className="loopino-strategy-preview-modal" role="dialog" aria-modal="true">
          <div className="loopino-strategy-preview-backdrop" onClick={closeImagePreview} />
          <div className="loopino-strategy-preview-card">
            <button type="button" className="loopino-strategy-preview-close" onClick={closeImagePreview} aria-label={t('strategy.preview.actions.close', { defaultValue: 'Chiudi' })}>×</button>
            {imageGallery.length > 1 && (
              <>
                <button type="button" className="loopino-strategy-preview-nav prev" onClick={prevImage} aria-label={t('strategy.preview.actions.previous', { defaultValue: 'Precedente' })}>‹</button>
                <button type="button" className="loopino-strategy-preview-nav next" onClick={nextImage} aria-label={t('strategy.preview.actions.next', { defaultValue: 'Successiva' })}>›</button>
                <div className="loopino-strategy-preview-counter">
                  {t('strategy.preview.counter', { current: currentImageIndex + 1, total: imageGallery.length, defaultValue: `${currentImageIndex + 1} / ${imageGallery.length}` })}
                </div>
              </>
            )}
            <img src={imagePreview} alt={t('strategy.preview.alt.image', { defaultValue: 'Anteprima immagine' })} />
          </div>
        </div>
      )}
    </div>
  );
};

const DesktopStrategyPage = (props) => (
  <DesktopAlertProvider>
    <DesktopStrategyPageContent {...props} />
  </DesktopAlertProvider>
);

export default DesktopStrategyPage;
